// localStorage seed for the account status.
//
// A hard refresh empties React Query's in-memory cache, so without a seed every
// reload blocks on a network round-trip behind a full-screen loader. Phase 90
// cached just the phone-verified boolean; this widens it to the whole status so
// the ROLE lookup stops flashing too (useCurrentRole had no cache at all, which
// is why /student/home showed "Loading…" on every single refresh).
//
// This is NOT a security boundary — RLS and the SECURITY DEFINER RPCs are. It
// only decides which chrome renders first; the server answer arrives moments
// later and always wins, INCLUDING a demotion (a stale cached 'superadmin' is
// dropped the instant the server disagrees). Worst case is one round-trip of an
// empty admin shell, because every query behind it is still RLS-gated.

import type { AccountStatus } from './account-api';

const PREFIX = 'account_status:';
/** Phase 90's key, superseded by this cache. Cleared on sign-out too. */
const LEGACY_PREFIX = 'phone_verified:';

function cacheKey(userId: string): string {
  return `${PREFIX}${userId}`;
}

export function readCachedStatus(userId: string): AccountStatus | undefined {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as Partial<AccountStatus>;
    // Reject a shape written by an older build rather than seeding the guards
    // with undefined fields.
    if (typeof parsed.profileComplete !== 'boolean' || typeof parsed.needsOtp !== 'boolean') {
      return undefined;
    }
    return parsed as AccountStatus;
  } catch {
    return undefined;
  }
}

export function writeCachedStatus(userId: string, status: AccountStatus): void {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(status));
  } catch {
    /* storage unavailable (private mode / SSR) — the query result still drives the UI */
  }
}

/** Drop every cached status. Called on sign-out so the next user starts clean. */
export function clearCachedStatus(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key !== null && (key.startsWith(PREFIX) || key.startsWith(LEGACY_PREFIX))) {
        doomed.push(key);
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
